const n = (value) => Number(value || 0);

const sum = (rows, key) => rows.reduce((total, row) => total + n(row[key]), 0);

const labelForYear = (yearOptions, yearId) =>
  yearOptions.find((year) => String(year.id) === String(yearId))?.yearLabel || (yearId ? `FY ${yearId}` : 'All Years');

const baseFilters = ({ yearOptions = [], filterYear = '', fromDate = '', toDate = '', asOnDate = '', date = '' }) => {
  const parts = [`Financial Year: ${labelForYear(yearOptions, filterYear)}`];
  if (asOnDate) parts.push(`As on: ${asOnDate}`);
  if (date) parts.push(`Date: ${date}`);
  if (fromDate) parts.push(`From: ${fromDate}`);
  if (toDate) parts.push(`To: ${toDate}`);
  return parts.join(' | ');
};

const categoryFundRows = (data) => {
  const categories = Array.isArray(data?.categories) ? data.categories : (Array.isArray(data) ? data : []);
  const commission = data?.commission
    ? [{
      category: 'Commission Income',
      openingBalance: data.commission.openingBalance || 0,
      income: data.commission.income || 0,
      expense: data.commission.expense || 0,
      closingBalance: data.commission.closingBalance || 0,
    }]
    : [];
  return [...categories, ...commission];
};

const flattenBalanceSheet = (data) => {
  const rows = [];
  const pushSection = (section, items = []) => {
    items.forEach((row) => rows.push({
      section,
      account: row.accountCode ? `${row.name} (${row.accountCode})` : row.name,
      amount: row.balance || 0,
    }));
  };

  (data?.assets?.subGroups || []).forEach((group) => pushSection(`Assets - ${group.name}`, group.rows));
(data?.liabilities?.subGroups || []).forEach((group) => pushSection(`Liabilities - ${group.name}`, group.rows));
  pushSection('Equity & Corpus', data?.equity?.rows || []);
  rows.push({ section: 'Equity & Corpus', account: 'Net Income for Period', amount: data?.incomeExpense?.surplus || data?.surplus || 0 });
  rows.push({ section: 'Totals', account: 'Total Assets', amount: data?.assets?.total || 0 });
  rows.push({ section: 'Totals', account: 'Total Liabilities + Equity', amount: data?.totalLiabilitiesEquity || 0 });
  return rows;
};

export const buildAccountingExportPayload = ({
  activeTab,
  data,
  activeDerasarId,
  yearOptions,
  filterYear,
  fromDate,
  toDate,
  asOnDate,
  bankAccountId,
}) => {
  const common = {
    derasarId: activeDerasarId,
    templateName: activeTab,
    filters: baseFilters({
      yearOptions,
      filterYear,
      fromDate: ['category-fund', 'cash-book', 'gold-register', 'item-register', 'income-expenditure'].includes(activeTab) ? fromDate : '',
      toDate: ['category-fund', 'cash-book', 'gold-register', 'item-register', 'income-expenditure'].includes(activeTab) ? toDate : '',
      asOnDate: ['balance-sheet', 'trial-balance'].includes(activeTab) ? asOnDate : '',
      date: activeTab === 'day-book' ? fromDate : '',
    }),
  };

  if (activeTab === 'balance-sheet') {
    return {
      ...common,
      title: 'Balance Sheet',
      tAccount: true,
      rawData: data,
      columns: [{ key: '_', label: '_' }], // placeholder — backend uses rawData
      rows: [],
    };
  }

  if (activeTab === 'trial-balance') {
    return {
      ...common,
      title: 'Trial Balance',
      tAccount: true,
      rawData: data,
      columns: [{ key: '_', label: '_' }],
      rows: [],
    };
  }

  if (activeTab === 'income-expenditure') {
    return {
      ...common,
      title: 'Income & Expenditure Account',
      tAccount: true,
      rawData: data,
      columns: [{ key: '_', label: '_' }],
      rows: [],
    };
  }

  if (activeTab === 'category-fund') {
    const rows = categoryFundRows(data);
    return {
      ...common,
      title: 'Category Fund Report',
      columns: [
        { key: 'category', label: 'Category', weight: 1.8 },
        { key: 'openingBalance', label: 'Opening', type: 'currency', align: 'right' },
        { key: 'income', label: 'Income', type: 'currency', align: 'right' },
        { key: 'expense', label: 'Expense', type: 'currency', align: 'right' },
        { key: 'closingBalance', label: 'Closing', type: 'currency', align: 'right' },
      ],
      rows,
      totals: {
        category: 'Grand Total',
        openingBalance: sum(rows, 'openingBalance'),
        income: sum(rows, 'income'),
        expense: sum(rows, 'expense'),
        closingBalance: sum(rows, 'closingBalance'),
      },
    };
  }

  if (activeTab === 'day-book') {
    return {
      ...common,
      title: 'Day Book',
      columns: [
        { key: 'voucherNo', label: 'Voucher No', weight: 1 },
        { key: 'entryType', label: 'Type', weight: 0.8 },
        { key: 'narration', label: 'Narration', weight: 2.4 },
        { key: 'totalDr', label: 'Dr', type: 'currency', align: 'right' },
        { key: 'totalCr', label: 'Cr', type: 'currency', align: 'right' },
      ],
      rows: data?.rows || [],
      totals: { voucherNo: 'Net', narration: 'Receipts - Payments', totalDr: data?.summary?.totalReceipts || 0, totalCr: data?.summary?.totalPayments || 0 },
    };
  }

  if (activeTab === 'cash-book') {
    return {
      ...common,
      title: 'Cash Book',
      columns: [
        { key: 'date',      label: 'Date',           type: 'date',     weight: 0.9 },
        { key: 'voucherNo', label: 'Voucher No',                        weight: 1.0 },
        { key: 'payeeName', label: 'Payee / Member',                    weight: 1.2 },
        { key: 'narration', label: 'Narration',                         weight: 2.0 },
        { key: 'debit',     label: 'Dr',              type: 'currency', align: 'right' },
        { key: 'credit',    label: 'Cr',              type: 'currency', align: 'right' },
        { key: 'balance',   label: 'Balance',         type: 'currency', align: 'right' },
      ],
      rows: data?.rows || [],
      totals: { voucherNo: 'Closing Balance', balance: data?.closingBalance || 0 },
    };
  }

  if (activeTab === 'bank-book') {
    const allRows     = data?.rows              || [];
    const bankAccounts = data?.bankAccounts     || [];
    const bankOpening  = data?.bankOpeningBalances || {};

    const selectedBank = bankAccountId
      ? bankAccounts.find((a) => String(a.id) === String(bankAccountId))
      : null;

    const accountsToExport = selectedBank ? [selectedBank] : bankAccounts;

    const bankGroups = accountsToExport.map((account) => {
      const rows  = allRows.filter((r) => String(r.bankAccountId) === String(account.id));
      const ob    = Number(bankOpening[account.id] || 0);
      let running = ob;
      const rowsWithBal = rows.map((r) => {
        running += Number(r.debit || 0) - Number(r.credit || 0);
        return { ...r, runBal: running };
      });
      const cb      = rowsWithBal.length ? rowsWithBal[rowsWithBal.length - 1].runBal : ob;
      const totalDr = rows.reduce((s, r) => s + Number(r.debit  || 0), 0);
      const totalCr = rows.reduce((s, r) => s + Number(r.credit || 0), 0);
      return { bankName: account.name, rows: rowsWithBal, openingBalance: ob, closingBalance: cb, totalDr, totalCr };
    });

    return {
      ...common,
      title:        selectedBank ? `Bank Book - ${selectedBank.name}` : 'Bank Book',
      filters:      common.filters + (selectedBank ? `  |  Bank: ${selectedBank.name}` : ''),
      tAccount:     true,
      templateName: 'bank-book',
      rawData:      { bankGroups, openingBalance: data?.openingBalance || 0, closingBalance: data?.closingBalance || 0 },
      columns:      [{ key: '_', label: '_' }],
      rows:         [],
    };
  }

  if (activeTab === 'gold-register') {
    return {
      ...common,
      title: 'Gold Register',
      columns: [
        { key: 'date', label: 'Date', type: 'date', weight: 0.9 },
        { key: 'receiptNo', label: 'Receipt No', weight: 1 },
        { key: 'category', label: 'Category', weight: 1.4 },
        { key: 'goldGrams', label: 'Grams', type: 'number', align: 'right' },
        { key: 'goldPurity', label: 'Purity', align: 'right' },
        { key: 'valuationRate', label: 'Rate/g', type: 'currency', align: 'right' },
        { key: 'amountInr', label: 'Value', type: 'currency', align: 'right' },
      ],
      rows: data?.receipts || [],
      totals: { receiptNo: 'Total', goldGrams: data?.totalGrams || 0, amountInr: data?.totalInr || 0 },
    };
  }

  if (activeTab === 'item-register') {
    return {
      ...common,
      title: 'Item Register',
      columns: [
        { key: 'receiptDate', label: 'Date', type: 'date' },
        { key: 'receiptNo', label: 'Receipt No' },
        { key: 'category', label: 'Category', weight: 1.3 },
        { key: 'narration', label: 'Description', weight: 2 },
        { key: 'amountInr', label: 'Estimated Value', type: 'currency', align: 'right' },
      ],
      rows: (data?.receipts || []).map((receipt) => ({ ...receipt, category: receipt.donationCategory?.name || '' })),
      totals: { receiptNo: 'Total', category: `${data?.totalItems || 0} items`, amountInr: data?.totalInr || 0 },
    };
  }

  return null;
};

export const buildMemberExportPayload = ({
  activeTab,
  activeDerasarId,
  filterYear,
  yearOptions,
  filterCategoryId,
  categories,
  viewMode,
  rows,
  title,
}) => {
  const category = categories.find((item) => String(item.id) === String(filterCategoryId));
  return {
    derasarId: activeDerasarId,
    title,
    templateName: activeTab,
    filters: [
      `Financial Year: ${labelForYear(yearOptions, filterYear)}`,
      category ? `Category: ${category.name}` : 'Category: All',
      viewMode ? `View: ${viewMode}` : '',
    ].filter(Boolean).join(' | '),
    ...rows,
  };
};
